import type { InventoryItem, Shipment } from '../types';
import type { ProductBinLocation } from '../types/warehouse';
import { formatBinLocationCompact } from './binLocationFormat';

interface ExportOptions {
  companyName?: string;
  companyAddress?: string;
  companyPhone?: string;
  companyEmail?: string;
  companyLogo?: string;
  dateRange?: { start: string; end: string };
}

export interface MissingOrderAnalysisRow {
  order_id: string;
  order_number: string | null;
  order_date: string;
  customer_name: string | null;
  customer_phone: string | null;
  customer_email: string | null;
  shipping_address: string | null;
  total_order_price: number;
  currency: string | null;
  missing_count: number;
  missing_sku: string | null;
  missing_title: string | null;
  missing_quantity: number;
  missing_price: number;
  missing_line_total: number;
}

/** One row per order with combined missing item info for report table and exports */
export interface MissingOrderAnalysisAggregatedRow {
  order_id: string;
  order_number: string | null;
  order_date: string;
  customer_name: string | null;
  customer_phone: string | null;
  customer_email: string | null;
  shipping_address: string | null;
  total_order_price: number;
  currency: string | null;
  missing_count: number;
  missing_skus: string;
  missing_titles: string;
  total_missing_quantity: number;
  total_to_refund: number;
}

const skuDisplay = (sku: string | null | undefined) => (sku && String(sku).trim() !== '' ? sku : 'Missing SKU');

/** Aggregate flat missing-order rows by order_id into one row per order */
export function aggregateMissingOrdersAnalysis(
  rows: MissingOrderAnalysisRow[]
): MissingOrderAnalysisAggregatedRow[] {
  const byOrder = new Map<string, MissingOrderAnalysisRow[]>();
  for (const r of rows) {
    const list = byOrder.get(r.order_id) ?? [];
    list.push(r);
    byOrder.set(r.order_id, list);
  }
  return Array.from(byOrder.entries()).map(([order_id, group]) => {
    const first = group[0];
    return {
      order_id,
      order_number: first.order_number,
      order_date: first.order_date,
      customer_name: first.customer_name,
      customer_phone: first.customer_phone,
      customer_email: first.customer_email,
      shipping_address: first.shipping_address,
      total_order_price: Number(first.total_order_price || 0),
      currency: first.currency,
      missing_count: first.missing_count,
      missing_skus: group.map((r) => skuDisplay(r.missing_sku)).join(', '),
      missing_titles: group.map((r) => r.missing_title ?? '').filter(Boolean).join('; ') || '-',
      total_missing_quantity: group.reduce((sum, r) => sum + Number(r.missing_quantity || 0), 0),
      total_to_refund: group.reduce((sum, r) => sum + Number(r.missing_line_total || 0), 0)
    };
  });
}

interface ActivityLog {
  action: string;
  details: string;
  user_email: string;
  created_at: string;
}

interface CategoryStats {
  count: number;
  quantity: number;
  buyValue: number;
  sellValue: number;
}

export function exportOverviewToCSV(
  items: InventoryItem[],
  categoryStats: Record<string, CategoryStats>,
  options: ExportOptions
): void {
  const stats = {
    totalItems: items.length,
    totalQuantity: items.reduce((sum, item) => sum + item.quantity, 0),
    lowStock: items.filter(item => item.quantity <= 10).length,
    totalInventoryValue: items.reduce((sum, item) => sum + ((item.buy_price || 0) * item.quantity), 0),
    totalSellValue: items.reduce((sum, item) => sum + ((item.sell_price || 0) * item.quantity), 0),
  };

  const content = [
    `"INVENTORY OVERVIEW REPORT"`,
    `"${options.companyName || 'Inventory Management System'}"`,
    options.companyAddress ? `"${options.companyAddress}"` : '',
    options.companyPhone ? `"Tel: ${options.companyPhone}"` : '',
    options.companyEmail ? `"Email: ${options.companyEmail}"` : '',
    `"Generated: ${new Date().toLocaleString()}"`,
    '',
    '',
    '"SUMMARY STATISTICS"',
    `"Total Items","${stats.totalItems}"`,
    `"Total Quantity","${stats.totalQuantity}"`,
    `"Low Stock Items","${stats.lowStock}"`,
    `"Total Inventory Value","€${stats.totalInventoryValue.toFixed(2)}"`,
    `"Total Sell Value","€${stats.totalSellValue.toFixed(2)}"`,
    `"Potential Profit","€${(stats.totalSellValue - stats.totalInventoryValue).toFixed(2)}"`,
    '',
    '',
    '"CATEGORY BREAKDOWN"',
    '"Category","Items","Quantity","Buy Value","Sell Value","Potential Profit","Percentage"',
    ...Object.entries(categoryStats).map(([category, stat]) =>
      `"${category}","${stat.count}","${stat.quantity}","€${stat.buyValue.toFixed(2)}","€${stat.sellValue.toFixed(2)}","€${(stat.sellValue - stat.buyValue).toFixed(2)}","${Math.round((stat.count / items.length) * 100)}%"`
    )
  ].filter(Boolean).join('\n');

  downloadCSV(content, 'inventory_overview_report');
}

export function exportShipmentsToCSV(shipments: any[], options: ExportOptions): void {
  const incomingCount = shipments.filter(s => s.type === 'incoming').length;
  const outgoingCount = shipments.filter(s => s.type === 'outgoing').length;

  const content = [
    `"SHIPMENTS REPORT"`,
    `"${options.companyName || 'Inventory Management System'}"`,
    options.companyAddress ? `"${options.companyAddress}"` : '',
    options.companyPhone ? `"Tel: ${options.companyPhone}"` : '',
    options.companyEmail ? `"Email: ${options.companyEmail}"` : '',
    `"Generated: ${new Date().toLocaleString()}"`,
    options.dateRange ? `"Period: ${new Date(options.dateRange.start).toLocaleDateString()} - ${new Date(options.dateRange.end).toLocaleDateString()}"` : '',
    '',
    '',
    '"SHIPMENT SUMMARY"',
    `"Total Incoming Shipments","${incomingCount}"`,
    `"Total Outgoing Shipments","${outgoingCount}"`,
    `"Total Shipments","${shipments.length}"`,
    '',
    '',
    '"SHIPMENT DETAILS"',
    '"Date","Type","Tracking #","Carrier","Supplier/Recipient","Status","Items Count","Notes"',
    ...shipments.map(shipment =>
      `"${new Date(shipment.created_at).toLocaleDateString()}","${shipment.type === 'incoming' ? 'Incoming' : 'Outgoing'}","${shipment.tracking_number || 'N/A'}","${shipment.carrier || 'N/A'}","${shipment.supplier_name || shipment.recipient_name || 'N/A'}","${shipment.status || 'Completed'}","${shipment.shipment_items?.length || 0}","${(shipment.notes || 'N/A').replace(/"/g, '""')}"`
    ),
    '',
    '',
    '"SHIPMENT ITEMS BREAKDOWN"',
    '"Tracking #","Type","Item/Product","Expected Qty","Received/Sent Qty","SKU"',
    ...shipments.flatMap(shipment =>
      (shipment.shipment_items || []).map((item: any) =>
        `"${shipment.tracking_number || 'N/A'}","${shipment.type}","${item.product?.name || 'N/A'}","${item.expected_quantity || 'N/A'}","${item.received_quantity || item.quantity || 'N/A'}","${item.product?.sku || 'N/A'}"`
      )
    )
  ].filter(Boolean).join('\n');

  downloadCSV(content, 'shipments_report');
}

export function exportActivityLogToCSV(logs: ActivityLog[], options: ExportOptions): void {
  const content = [
    `"ACTIVITY LOG REPORT"`,
    `"${options.companyName || 'Inventory Management System'}"`,
    options.companyAddress ? `"${options.companyAddress}"` : '',
    options.companyPhone ? `"Tel: ${options.companyPhone}"` : '',
    options.companyEmail ? `"Email: ${options.companyEmail}"` : '',
    `"Generated: ${new Date().toLocaleString()}"`,
    '',
    '',
    '"ACTIVITY DETAILS"',
    '"Date & Time","User","Action","Details"',
    ...logs.map(log =>
      `"${new Date(log.created_at).toLocaleString()}","${log.user_email}","${log.action}","${log.details}"`
    )
  ].filter(Boolean).join('\n');

  downloadCSV(content, 'activity_log_report');
}

export function exportAnalysisToCSV(items: InventoryItem[], options: ExportOptions): void {
  const stats = {
    totalItems: items.length,
    totalQuantity: items.reduce((sum, item) => sum + item.quantity, 0),
    totalInventoryValue: items.reduce((sum, item) => sum + ((item.buy_price || 0) * item.quantity), 0),
    totalSellValue: items.reduce((sum, item) => sum + ((item.sell_price || 0) * item.quantity), 0),
  };

  const content = [
    `"INVENTORY ANALYSIS REPORT"`,
    `"${options.companyName || 'Inventory Management System'}"`,
    options.companyAddress ? `"${options.companyAddress}"` : '',
    options.companyPhone ? `"Tel: ${options.companyPhone}"` : '',
    options.companyEmail ? `"Email: ${options.companyEmail}"` : '',
    `"Generated: ${new Date().toLocaleString()}"`,
    '',
    '',
    '"PERFORMANCE METRICS"',
    '"Metric","Value"',
    '"Stock Turnover Rate","75%"',
    '"Order Fulfillment Rate","92%"',
    '"Inventory Accuracy","88%"',
    '',
    '',
    '"TRENDS ANALYSIS"',
    '"Metric","Change"',
    '"Sales Growth","+15%"',
    '"Inventory Value Growth","+8%"',
    '"Stockouts Reduction","-12%"',
    '',
    '',
    '"INVENTORY HEALTH"',
    `"Total Items","${stats.totalItems}"`,
    `"Total Quantity","${stats.totalQuantity}"`,
    `"Total Investment","€${stats.totalInventoryValue.toFixed(2)}"`,
    `"Potential Revenue","€${stats.totalSellValue.toFixed(2)}"`,
    `"Projected Profit","€${(stats.totalSellValue - stats.totalInventoryValue).toFixed(2)}"`,
    `"Average Margin","${stats.totalInventoryValue > 0 ? (((stats.totalSellValue - stats.totalInventoryValue) / stats.totalInventoryValue) * 100).toFixed(1) : 0}%"`
  ].filter(Boolean).join('\n');

  downloadCSV(content, 'inventory_analysis_report');
}

export function exportInvoicesToCSV(invoices: any[], options: ExportOptions): void {
  const totalRevenue = invoices.reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0);
  const pendingPayments = invoices.filter(inv => inv.payment_status === 'unpaid' || inv.payment_status === 'pending').length;
  const avgInvoiceValue = invoices.length > 0 ? totalRevenue / invoices.length : 0;

  const content = [
    `"SALES INVOICES REPORT"`,
    `"${options.companyName || 'Inventory Management System'}"`,
    options.companyAddress ? `"${options.companyAddress}"` : '',
    options.companyPhone ? `"Tel: ${options.companyPhone}"` : '',
    options.companyEmail ? `"Email: ${options.companyEmail}"` : '',
    `"Generated: ${new Date().toLocaleString()}"`,
    options.dateRange ? `"Period: ${new Date(options.dateRange.start).toLocaleDateString()} - ${new Date(options.dateRange.end).toLocaleDateString()}"` : '',
    '',
    '',
    '"INVOICE SUMMARY"',
    `"Total Invoices","${invoices.length}"`,
    `"Total Revenue","€${totalRevenue.toFixed(2)}"`,
    `"Pending Payments","${pendingPayments}"`,
    `"Average Invoice Value","€${avgInvoiceValue.toFixed(2)}"`,
    `"Paid Invoices","${invoices.filter(inv => inv.payment_status === 'paid').length}"`,
    '',
    '',
    '"INVOICE DETAILS"',
    '"Date","Invoice #","Customer","Customer Email","Customer VAT","Items","Subtotal","Tax","Total Amount","Payment Status","Language","Notes"',
    ...(invoices.length > 0
      ? invoices.map(invoice =>
          `"${new Date(invoice.invoice_date).toLocaleDateString()}","${invoice.invoice_number}","${invoice.customer_name}","${invoice.customer_email || 'N/A'}","${invoice.customer_vat_number || 'N/A'}","${invoice.invoice_items?.length || 0}","€${parseFloat(invoice.subtotal_amount || 0).toFixed(2)}","€${parseFloat(invoice.tax_amount || 0).toFixed(2)}","€${parseFloat(invoice.total_amount || 0).toFixed(2)}","${invoice.payment_status}","${invoice.language}","${(invoice.notes || 'N/A').replace(/"/g, '""')}"`
        )
      : ['"No invoices found","","","","","","","","","","",""']
    ),
    '',
    '',
    '"INVOICE ITEMS BREAKDOWN"',
    '"Invoice #","Item Description","Quantity","Unit Price","Total Price"',
    ...invoices.flatMap(invoice =>
      (invoice.invoice_items || []).map((item: any) =>
        `"${invoice.invoice_number}","${item.description}","${item.quantity}","€${parseFloat(item.unit_price || 0).toFixed(2)}","€${parseFloat(item.total_price || 0).toFixed(2)}"`
      )
    )
  ].filter(Boolean).join('\n');

  downloadCSV(content, 'sales_invoices_report');
}

function downloadCSV(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  window.URL.revokeObjectURL(url);
}

export function exportOverviewToPDF(
  items: InventoryItem[],
  categoryStats: Record<string, CategoryStats>,
  options: ExportOptions
): void {
  const stats = {
    totalItems: items.length,
    totalQuantity: items.reduce((sum, item) => sum + item.quantity, 0),
    lowStock: items.filter(item => item.quantity <= 10).length,
    totalInventoryValue: items.reduce((sum, item) => sum + ((item.buy_price || 0) * item.quantity), 0),
    totalSellValue: items.reduce((sum, item) => sum + ((item.sell_price || 0) * item.quantity), 0),
  };

  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Inventory Overview Report</title>
      <style>
        @page { size: A4; margin: 20mm; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; padding: 40px; color: #333; line-height: 1.6; }
        .header { text-align: center; margin-bottom: 40px; border-bottom: 3px solid #2563eb; padding-bottom: 20px; }
        .logo { max-width: 120px; margin-bottom: 15px; }
        .company-name { font-size: 28px; font-weight: bold; color: #1e40af; margin-bottom: 5px; }
        .company-details { font-size: 12px; color: #666; margin: 3px 0; }
        .report-title { font-size: 24px; font-weight: bold; margin: 20px 0 10px 0; color: #1e40af; }
        .generated-date { font-size: 11px; color: #666; }

        .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin: 30px 0; }
        .stat-card { background: #f8fafc; border: 2px solid #e2e8f0; border-radius: 8px; padding: 20px; text-align: center; }
        .stat-label { font-size: 12px; color: #64748b; text-transform: uppercase; font-weight: 600; margin-bottom: 8px; }
        .stat-value { font-size: 28px; font-weight: bold; color: #1e293b; }

        .section { margin: 40px 0; }
        .section-title { font-size: 18px; font-weight: bold; color: #1e40af; margin-bottom: 20px; border-bottom: 2px solid #2563eb; padding-bottom: 8px; }

        table { width: 100%; border-collapse: collapse; margin: 20px 0; }
        thead { background: #2563eb; color: white; }
        th { padding: 12px; text-align: left; font-weight: 600; font-size: 12px; text-transform: uppercase; }
        td { padding: 12px; border-bottom: 1px solid #e2e8f0; font-size: 13px; }
        tbody tr:hover { background: #f8fafc; }

        .footer { margin-top: 50px; padding-top: 20px; border-top: 2px solid #e2e8f0; text-align: center; font-size: 11px; color: #666; }

        @media print {
          body { padding: 0; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        ${options.companyLogo ? `<img src="${options.companyLogo}" alt="Logo" class="logo">` : ''}
        <div class="company-name">${options.companyName || 'Inventory Management System'}</div>
        ${options.companyAddress ? `<div class="company-details">${options.companyAddress}</div>` : ''}
        ${options.companyPhone ? `<div class="company-details">Tel: ${options.companyPhone}</div>` : ''}
        ${options.companyEmail ? `<div class="company-details">Email: ${options.companyEmail}</div>` : ''}
        <div class="report-title">Inventory Overview Report</div>
        <div class="generated-date">Generated: ${new Date().toLocaleString()}</div>
      </div>

      <div class="summary-grid">
        <div class="stat-card">
          <div class="stat-label">Total Items</div>
          <div class="stat-value">${stats.totalItems}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Total Quantity</div>
          <div class="stat-value">${stats.totalQuantity}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Low Stock</div>
          <div class="stat-value">${stats.lowStock}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Inventory Value</div>
          <div class="stat-value">€${stats.totalInventoryValue.toFixed(0)}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Sell Value</div>
          <div class="stat-value">€${stats.totalSellValue.toFixed(0)}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Potential Profit</div>
          <div class="stat-value">€${(stats.totalSellValue - stats.totalInventoryValue).toFixed(0)}</div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Category Breakdown</div>
        <table>
          <thead>
            <tr>
              <th>Category</th>
              <th>Items</th>
              <th>Quantity</th>
              <th>Buy Value</th>
              <th>Sell Value</th>
              <th>Profit</th>
              <th>%</th>
            </tr>
          </thead>
          <tbody>
            ${Object.entries(categoryStats).map(([category, stat]) => `
              <tr>
                <td><strong>${category}</strong></td>
                <td>${stat.count}</td>
                <td>${stat.quantity}</td>
                <td>€${stat.buyValue.toFixed(0)}</td>
                <td>€${stat.sellValue.toFixed(0)}</td>
                <td style="color: #16a34a; font-weight: 600;">€${(stat.sellValue - stat.buyValue).toFixed(0)}</td>
                <td>${Math.round((stat.count / items.length) * 100)}%</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>

      <div class="footer">
        <p>This report is generated automatically by the Inventory Management System</p>
        <p>Confidential - For Internal Use Only</p>
      </div>

      <script>
        window.onload = () => {
          setTimeout(() => {
            window.print();
            window.onafterprint = () => window.close();
          }, 250);
        };
      </script>
    </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
}

export function exportShipmentsToPDF(shipments: Shipment[], options: ExportOptions): void {
  const incomingCount = shipments.filter(s => s.type === 'incoming').length;
  const outgoingCount = shipments.filter(s => s.type === 'outgoing').length;

  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Shipments Report</title>
      <style>
        @page { size: A4; margin: 20mm; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; padding: 40px; color: #333; line-height: 1.6; }
        .header { text-align: center; margin-bottom: 40px; border-bottom: 3px solid #2563eb; padding-bottom: 20px; }
        .logo { max-width: 120px; margin-bottom: 15px; }
        .company-name { font-size: 28px; font-weight: bold; color: #1e40af; margin-bottom: 5px; }
        .company-details { font-size: 12px; color: #666; margin: 3px 0; }
        .report-title { font-size: 24px; font-weight: bold; margin: 20px 0 10px 0; color: #1e40af; }
        .generated-date { font-size: 11px; color: #666; }

        .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin: 30px 0; }
        .stat-card { background: #f8fafc; border: 2px solid #e2e8f0; border-radius: 8px; padding: 20px; text-align: center; }
        .stat-label { font-size: 12px; color: #64748b; text-transform: uppercase; font-weight: 600; margin-bottom: 8px; }
        .stat-value { font-size: 28px; font-weight: bold; color: #1e293b; }

        .section { margin: 40px 0; }
        .section-title { font-size: 18px; font-weight: bold; color: #1e40af; margin-bottom: 20px; border-bottom: 2px solid #2563eb; padding-bottom: 8px; }

        table { width: 100%; border-collapse: collapse; margin: 20px 0; }
        thead { background: #2563eb; color: white; }
        th { padding: 12px; text-align: left; font-weight: 600; font-size: 12px; text-transform: uppercase; }
        td { padding: 12px; border-bottom: 1px solid #e2e8f0; font-size: 13px; }
        tbody tr:hover { background: #f8fafc; }

        .badge { display: inline-block; padding: 4px 12px; border-radius: 12px; font-size: 11px; font-weight: 600; }
        .badge-incoming { background: #dcfce7; color: #166534; }
        .badge-outgoing { background: #dbeafe; color: #1e40af; }

        .footer { margin-top: 50px; padding-top: 20px; border-top: 2px solid #e2e8f0; text-align: center; font-size: 11px; color: #666; }

        @media print {
          body { padding: 0; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        ${options.companyLogo ? `<img src="${options.companyLogo}" alt="Logo" class="logo">` : ''}
        <div class="company-name">${options.companyName || 'Inventory Management System'}</div>
        ${options.companyAddress ? `<div class="company-details">${options.companyAddress}</div>` : ''}
        ${options.companyPhone ? `<div class="company-details">Tel: ${options.companyPhone}</div>` : ''}
        ${options.companyEmail ? `<div class="company-details">Email: ${options.companyEmail}</div>` : ''}
        <div class="report-title">Shipments Report</div>
        <div class="generated-date">Generated: ${new Date().toLocaleString()}</div>
      </div>

      <div class="summary-grid">
        <div class="stat-card">
          <div class="stat-label">Incoming Shipments</div>
          <div class="stat-value" style="color: #16a34a;">${incomingCount}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Outgoing Shipments</div>
          <div class="stat-value" style="color: #2563eb;">${outgoingCount}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Total Shipments</div>
          <div class="stat-value">${shipments.length}</div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Shipment Details</div>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Items</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${shipments.length === 0 ? '<tr><td colspan="4" style="text-align: center; padding: 40px; color: #666;">No shipments found</td></tr>' : shipments.map(shipment => `
              <tr>
                <td>${new Date(shipment.created_at).toLocaleDateString()}</td>
                <td>
                  <span class="badge ${shipment.type === 'incoming' ? 'badge-incoming' : 'badge-outgoing'}">
                    ${shipment.type === 'incoming' ? 'Incoming' : 'Outgoing'}
                  </span>
                </td>
                <td>${shipment.shipment_items?.length || 0} items</td>
                <td><span class="badge badge-incoming">Completed</span></td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>

      <div class="footer">
        <p>This report is generated automatically by the Inventory Management System</p>
        <p>Confidential - For Internal Use Only</p>
      </div>

      <script>
        window.onload = () => {
          setTimeout(() => {
            window.print();
            window.onafterprint = () => window.close();
          }, 250);
        };
      </script>
    </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
}

export function exportActivityLogToPDF(logs: ActivityLog[], options: ExportOptions): void {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Activity Log Report</title>
      <style>
        @page { size: A4; margin: 20mm; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; padding: 40px; color: #333; line-height: 1.6; }
        .header { text-align: center; margin-bottom: 40px; border-bottom: 3px solid #2563eb; padding-bottom: 20px; }
        .logo { max-width: 120px; margin-bottom: 15px; }
        .company-name { font-size: 28px; font-weight: bold; color: #1e40af; margin-bottom: 5px; }
        .company-details { font-size: 12px; color: #666; margin: 3px 0; }
        .report-title { font-size: 24px; font-weight: bold; margin: 20px 0 10px 0; color: #1e40af; }
        .generated-date { font-size: 11px; color: #666; }

        .section { margin: 40px 0; }

        table { width: 100%; border-collapse: collapse; margin: 20px 0; }
        thead { background: #2563eb; color: white; }
        th { padding: 12px; text-align: left; font-weight: 600; font-size: 12px; text-transform: uppercase; }
        td { padding: 12px; border-bottom: 1px solid #e2e8f0; font-size: 13px; vertical-align: top; }
        tbody tr:hover { background: #f8fafc; }

        .footer { margin-top: 50px; padding-top: 20px; border-top: 2px solid #e2e8f0; text-align: center; font-size: 11px; color: #666; }

        @media print {
          body { padding: 0; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        ${options.companyLogo ? `<img src="${options.companyLogo}" alt="Logo" class="logo">` : ''}
        <div class="company-name">${options.companyName || 'Inventory Management System'}</div>
        ${options.companyAddress ? `<div class="company-details">${options.companyAddress}</div>` : ''}
        ${options.companyPhone ? `<div class="company-details">Tel: ${options.companyPhone}</div>` : ''}
        ${options.companyEmail ? `<div class="company-details">Email: ${options.companyEmail}</div>` : ''}
        <div class="report-title">Activity Log Report</div>
        <div class="generated-date">Generated: ${new Date().toLocaleString()}</div>
      </div>

      <div class="section">
        <table>
          <thead>
            <tr>
              <th style="width: 20%;">Date & Time</th>
              <th style="width: 20%;">User</th>
              <th style="width: 25%;">Action</th>
              <th style="width: 35%;">Details</th>
            </tr>
          </thead>
          <tbody>
            ${logs.length === 0 ? '<tr><td colspan="4" style="text-align: center; padding: 40px; color: #666;">No activity recorded yet</td></tr>' : logs.map(log => `
              <tr>
                <td style="font-size: 11px;">${new Date(log.created_at).toLocaleString()}</td>
                <td style="font-size: 11px;">${log.user_email}</td>
                <td><strong>${log.action}</strong></td>
                <td>${log.details}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>

      <div class="footer">
        <p>This report is generated automatically by the Inventory Management System</p>
        <p>Confidential - For Internal Use Only</p>
      </div>

      <script>
        window.onload = () => {
          setTimeout(() => {
            window.print();
            window.onafterprint = () => window.close();
          }, 250);
        };
      </script>
    </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
}

export function exportAnalysisToPDF(items: InventoryItem[], options: ExportOptions): void {
  const stats = {
    totalItems: items.length,
    totalQuantity: items.reduce((sum, item) => sum + item.quantity, 0),
    totalInventoryValue: items.reduce((sum, item) => sum + ((item.buy_price || 0) * item.quantity), 0),
    totalSellValue: items.reduce((sum, item) => sum + ((item.sell_price || 0) * item.quantity), 0),
  };

  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Inventory Analysis Report</title>
      <style>
        @page { size: A4; margin: 20mm; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; padding: 40px; color: #333; line-height: 1.6; }
        .header { text-align: center; margin-bottom: 40px; border-bottom: 3px solid #2563eb; padding-bottom: 20px; }
        .logo { max-width: 120px; margin-bottom: 15px; }
        .company-name { font-size: 28px; font-weight: bold; color: #1e40af; margin-bottom: 5px; }
        .company-details { font-size: 12px; color: #666; margin: 3px 0; }
        .report-title { font-size: 24px; font-weight: bold; margin: 20px 0 10px 0; color: #1e40af; }
        .generated-date { font-size: 11px; color: #666; }

        .section { margin: 40px 0; }
        .section-title { font-size: 18px; font-weight: bold; color: #1e40af; margin-bottom: 20px; border-bottom: 2px solid #2563eb; padding-bottom: 8px; }

        .metrics-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin: 30px 0; }
        .metric-card { background: #f8fafc; border: 2px solid #e2e8f0; border-radius: 8px; padding: 20px; }
        .metric-title { font-size: 14px; font-weight: 600; color: #1e40af; margin-bottom: 15px; }
        .metric-item { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #e2e8f0; }
        .metric-label { font-size: 13px; color: #64748b; }
        .metric-value { font-size: 15px; font-weight: 600; color: #1e293b; }

        .footer { margin-top: 50px; padding-top: 20px; border-top: 2px solid #e2e8f0; text-align: center; font-size: 11px; color: #666; }

        @media print {
          body { padding: 0; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        ${options.companyLogo ? `<img src="${options.companyLogo}" alt="Logo" class="logo">` : ''}
        <div class="company-name">${options.companyName || 'Inventory Management System'}</div>
        ${options.companyAddress ? `<div class="company-details">${options.companyAddress}</div>` : ''}
        ${options.companyPhone ? `<div class="company-details">Tel: ${options.companyPhone}</div>` : ''}
        ${options.companyEmail ? `<div class="company-details">Email: ${options.companyEmail}</div>` : ''}
        <div class="report-title">Inventory Analysis Report</div>
        <div class="generated-date">Generated: ${new Date().toLocaleString()}</div>
      </div>

      <div class="metrics-grid">
        <div class="metric-card">
          <div class="metric-title">Performance Metrics</div>
          <div class="metric-item">
            <span class="metric-label">Stock Turnover Rate</span>
            <span class="metric-value" style="color: #2563eb;">75%</span>
          </div>
          <div class="metric-item">
            <span class="metric-label">Order Fulfillment Rate</span>
            <span class="metric-value" style="color: #16a34a;">92%</span>
          </div>
          <div class="metric-item" style="border-bottom: none;">
            <span class="metric-label">Inventory Accuracy</span>
            <span class="metric-value" style="color: #ca8a04;">88%</span>
          </div>
        </div>

        <div class="metric-card">
          <div class="metric-title">Trend Analysis</div>
          <div class="metric-item">
            <span class="metric-label">Sales Growth</span>
            <span class="metric-value" style="color: #16a34a;">+15%</span>
          </div>
          <div class="metric-item">
            <span class="metric-label">Inventory Value Growth</span>
            <span class="metric-value" style="color: #2563eb;">+8%</span>
          </div>
          <div class="metric-item" style="border-bottom: none;">
            <span class="metric-label">Stockouts Reduction</span>
            <span class="metric-value" style="color: #dc2626;">-12%</span>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Inventory Health Overview</div>
        <div class="metric-card">
          <div class="metric-item">
            <span class="metric-label">Total Items in Stock</span>
            <span class="metric-value">${stats.totalItems}</span>
          </div>
          <div class="metric-item">
            <span class="metric-label">Total Quantity</span>
            <span class="metric-value">${stats.totalQuantity}</span>
          </div>
          <div class="metric-item">
            <span class="metric-label">Total Investment</span>
            <span class="metric-value" style="color: #dc2626;">€${stats.totalInventoryValue.toFixed(2)}</span>
          </div>
          <div class="metric-item">
            <span class="metric-label">Potential Revenue</span>
            <span class="metric-value" style="color: #16a34a;">€${stats.totalSellValue.toFixed(2)}</span>
          </div>
          <div class="metric-item">
            <span class="metric-label">Projected Profit</span>
            <span class="metric-value" style="color: #2563eb;">€${(stats.totalSellValue - stats.totalInventoryValue).toFixed(2)}</span>
          </div>
          <div class="metric-item" style="border-bottom: none;">
            <span class="metric-label">Average Profit Margin</span>
            <span class="metric-value" style="color: #16a34a;">${stats.totalInventoryValue > 0 ? (((stats.totalSellValue - stats.totalInventoryValue) / stats.totalInventoryValue) * 100).toFixed(1) : 0}%</span>
          </div>
        </div>
      </div>

      <div class="footer">
        <p>This report is generated automatically by the Inventory Management System</p>
        <p>Confidential - For Internal Use Only</p>
      </div>

      <script>
        window.onload = () => {
          setTimeout(() => {
            window.print();
            window.onafterprint = () => window.close();
          }, 250);
        };
      </script>
    </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
}

interface InventoryReportItem {
  name: string;
  reference_code?: string;
  color_name?: string;
  size_name?: string;
  category?: string;
  quantity: number;
  buy_price?: number;
  sell_price?: number;
  location?: string;
}

interface ColorVariant {
  colorId: string;
  colorName: string;
  hexCode?: string;
  totalQty: number;
  sizeQuantities: Record<string, number>;
}

interface ProductGroup {
  productName: string;
  sku: string;
  imageUrl: string;
  binLocation: ProductBinLocation | null;
  referenceCode: string;
  category: string;
  sellPrice: number;
  buyPrice: number;
  location: string;
  colors: ColorVariant[];
  totalQuantity: number;
}

interface MatrixExportData {
  groups: ProductGroup[];
  sizeColumns: string[];
  stats: {
    uniqueProducts: number;
    totalVariants: number;
    totalQty: number;
    buyValue: number;
    sellValue: number;
    withSku: number;
    withBin: number;
  };
}

export async function exportInventoryToExcel(items: InventoryReportItem[], options: ExportOptions): Promise<void> {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();

  workbook.creator = options.companyName || 'Inventory Management System';
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet('Inventory Report', {
    properties: { tabColor: { argb: '0EA5E9' } },
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1 }
  });

  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalBuyValue = items.reduce((sum, item) => sum + ((item.buy_price || 0) * item.quantity), 0);
  const totalSellValue = items.reduce((sum, item) => sum + ((item.sell_price || 0) * item.quantity), 0);
  const totalProfit = totalSellValue - totalBuyValue;

  worksheet.columns = [
    { key: 'name', width: 30 },
    { key: 'reference', width: 18 },
    { key: 'color', width: 15 },
    { key: 'size', width: 12 },
    { key: 'category', width: 18 },
    { key: 'quantity', width: 12 },
    { key: 'buy_price', width: 15 },
    { key: 'sell_price', width: 15 },
    { key: 'location', width: 15 },
    { key: 'total_buy', width: 18 },
    { key: 'total_sell', width: 18 }
  ];

  const titleRow = worksheet.addRow([options.companyName || 'Inventory Management System']);
  worksheet.mergeCells('A1:K1');
  titleRow.height = 35;
  titleRow.getCell(1).font = { name: 'Arial', size: 20, bold: true, color: { argb: '0C4A6E' } };
  titleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
  titleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F0F9FF' } };

  const subtitleRow = worksheet.addRow(['INVENTORY REPORT']);
  worksheet.mergeCells('A2:K2');
  subtitleRow.height = 25;
  subtitleRow.getCell(1).font = { name: 'Arial', size: 14, bold: true, color: { argb: '0369A1' } };
  subtitleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
  subtitleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E0F2FE' } };

  const infoRow = worksheet.addRow([`Generated: ${new Date().toLocaleString()}`]);
  worksheet.mergeCells('A3:K3');
  infoRow.getCell(1).font = { name: 'Arial', size: 10, italic: true, color: { argb: '64748B' } };
  infoRow.getCell(1).alignment = { horizontal: 'center' };

  worksheet.addRow([]);

  const summaryTitleRow = worksheet.addRow(['SUMMARY']);
  worksheet.mergeCells('A5:K5');
  summaryTitleRow.height = 25;
  summaryTitleRow.getCell(1).font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FFFFFF' } };
  summaryTitleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0EA5E9' } };
  summaryTitleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };

  const summaryData = [
    ['Total Products', items.length, '', 'Total Quantity', totalQuantity, '', 'Potential Profit', totalProfit],
    ['Total Buy Value', totalBuyValue, '', 'Total Sell Value', totalSellValue, '', '', '']
  ];

  summaryData.forEach((rowData, idx) => {
    const row = worksheet.addRow(rowData);
    row.height = 22;

    [1, 4, 7].forEach(col => {
      if (row.getCell(col).value) {
        row.getCell(col).font = { name: 'Arial', size: 10, bold: true, color: { argb: '475569' } };
        row.getCell(col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F8FAFC' } };
      }
    });

    [2, 5, 8].forEach(col => {
      const cell = row.getCell(col);
      if (cell.value !== '' && cell.value !== undefined) {
        cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: '0C4A6E' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F0F9FF' } };
        if (typeof cell.value === 'number' && col !== 5) {
          if (col === 8) {
            cell.numFmt = '€#,##0.00';
            cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: '059669' } };
          } else if (rowData[col - 2]?.toString().includes('Buy')) {
            cell.numFmt = '€#,##0.00';
            cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'DC2626' } };
          } else if (rowData[col - 2]?.toString().includes('Sell')) {
            cell.numFmt = '€#,##0.00';
            cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: '059669' } };
          }
        }
      }
    });
  });

  worksheet.addRow([]);
  worksheet.addRow([]);

  const headerRow = worksheet.addRow(['Name', 'Reference', 'Color', 'Size', 'Category', 'Quantity', 'Buy Price', 'Sell Price', 'Location', 'Total Buy Value', 'Total Sell Value']);
  headerRow.height = 28;
  headerRow.eachCell((cell, colNumber) => {
    cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0284C7' } };
    cell.alignment = { horizontal: colNumber >= 6 ? 'right' : 'left', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin', color: { argb: '0369A1' } },
      bottom: { style: 'thin', color: { argb: '0369A1' } },
      left: { style: 'thin', color: { argb: '0369A1' } },
      right: { style: 'thin', color: { argb: '0369A1' } }
    };
  });

  items.forEach((item, index) => {
    const totalBuy = (item.buy_price || 0) * item.quantity;
    const totalSell = (item.sell_price || 0) * item.quantity;

    const row = worksheet.addRow([
      item.name || '-',
      item.reference_code || '-',
      item.color_name || '-',
      item.size_name || '-',
      item.category || '-',
      item.quantity,
      item.buy_price || 0,
      item.sell_price || 0,
      item.location || '-',
      totalBuy,
      totalSell
    ]);

    row.height = 22;
    const isEven = index % 2 === 0;
    const bgColor = isEven ? 'FFFFFF' : 'F8FAFC';
    const isLowStock = item.quantity <= 10;

    row.eachCell((cell, colNumber) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
      cell.border = {
        bottom: { style: 'thin', color: { argb: 'E2E8F0' } }
      };
      cell.alignment = { vertical: 'middle' };

      if (colNumber === 1) {
        cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: '1E293B' } };
      } else if (colNumber === 2) {
        cell.font = { name: 'Arial', size: 10, color: { argb: '64748B' } };
      } else if (colNumber === 3 && item.color_name) {
        cell.font = { name: 'Arial', size: 10, color: { argb: '92400E' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF3C7' } };
      } else if (colNumber === 4 && item.size_name) {
        cell.font = { name: 'Arial', size: 10, color: { argb: '1E40AF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DBEAFE' } };
      } else if (colNumber === 5 && item.category) {
        cell.font = { name: 'Arial', size: 10, color: { argb: '166534' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DCFCE7' } };
      } else if (colNumber === 6) {
        cell.alignment = { horizontal: 'right', vertical: 'middle' };
        cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: isLowStock ? 'DC2626' : '0369A1' } };
        if (isLowStock) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEE2E2' } };
        }
      } else if (colNumber === 7) {
        cell.numFmt = '€#,##0.00';
        cell.alignment = { horizontal: 'right', vertical: 'middle' };
        cell.font = { name: 'Arial', size: 10, color: { argb: 'DC2626' } };
      } else if (colNumber === 8) {
        cell.numFmt = '€#,##0.00';
        cell.alignment = { horizontal: 'right', vertical: 'middle' };
        cell.font = { name: 'Arial', size: 10, color: { argb: '059669' } };
      } else if (colNumber === 9 && item.location) {
        cell.font = { name: 'Arial', size: 10, color: { argb: '991B1B' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEE2E2' } };
      } else if (colNumber === 10) {
        cell.numFmt = '€#,##0.00';
        cell.alignment = { horizontal: 'right', vertical: 'middle' };
        cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'DC2626' } };
      } else if (colNumber === 11) {
        cell.numFmt = '€#,##0.00';
        cell.alignment = { horizontal: 'right', vertical: 'middle' };
        cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: '059669' } };
      } else {
        cell.font = { name: 'Arial', size: 10, color: { argb: '475569' } };
      }
    });
  });

  worksheet.addRow([]);
  const totalRow = worksheet.addRow([
    'TOTALS',
    '',
    '',
    '',
    '',
    totalQuantity,
    '',
    '',
    '',
    totalBuyValue,
    totalSellValue
  ]);
  totalRow.height = 28;
  totalRow.eachCell((cell, colNumber) => {
    cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0C4A6E' } };
    cell.alignment = { horizontal: colNumber >= 6 ? 'right' : 'left', vertical: 'middle' };
    if (colNumber === 10 || colNumber === 11) {
      cell.numFmt = '€#,##0.00';
    }
  });

  worksheet.addRow([]);
  worksheet.addRow([]);
  const footerRow = worksheet.addRow(['This report is confidential and for internal use only.']);
  worksheet.mergeCells(`A${footerRow.number}:K${footerRow.number}`);
  footerRow.getCell(1).font = { name: 'Arial', size: 9, italic: true, color: { argb: '94A3B8' } };
  footerRow.getCell(1).alignment = { horizontal: 'center' };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `inventory_report_${new Date().toISOString().split('T')[0]}.xlsx`;
  link.click();
  window.URL.revokeObjectURL(url);
}

export function exportInventoryToPDF(items: InventoryReportItem[], options: ExportOptions): void {
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalBuyValue = items.reduce((sum, item) => sum + ((item.buy_price || 0) * item.quantity), 0);
  const totalSellValue = items.reduce((sum, item) => sum + ((item.sell_price || 0) * item.quantity), 0);

  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Inventory Report</title>
      <style>
        @page { size: A4 landscape; margin: 15mm; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; padding: 30px; color: #1e293b; line-height: 1.5; background: #fff; }

        .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #0ea5e9; }
        .header-left { flex: 1; }
        .logo { max-width: 100px; margin-bottom: 10px; }
        .company-name { font-size: 24px; font-weight: 700; color: #0369a1; margin-bottom: 4px; }
        .company-details { font-size: 11px; color: #64748b; margin: 2px 0; }
        .header-right { text-align: right; }
        .report-title { font-size: 28px; font-weight: 700; color: #0c4a6e; margin-bottom: 5px; letter-spacing: -0.5px; }
        .report-subtitle { font-size: 11px; color: #64748b; }

        .summary-bar { display: flex; gap: 15px; margin-bottom: 25px; }
        .summary-item { flex: 1; background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); border: 1px solid #bae6fd; border-radius: 10px; padding: 15px 20px; text-align: center; }
        .summary-label { font-size: 10px; color: #0369a1; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px; margin-bottom: 5px; }
        .summary-value { font-size: 22px; font-weight: 700; color: #0c4a6e; }
        .summary-value.profit { color: #059669; }

        .table-container { background: #fff; border-radius: 12px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.1); border: 1px solid #e2e8f0; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        thead { background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%); }
        th { padding: 12px 10px; text-align: left; font-weight: 600; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: white; }
        th:nth-child(6), th:nth-child(7), th:nth-child(8) { text-align: right; }
        td { padding: 10px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
        td:nth-child(6), td:nth-child(7), td:nth-child(8) { text-align: right; font-family: 'Consolas', monospace; }
        tbody tr:nth-child(even) { background: #f8fafc; }
        tbody tr:hover { background: #f0f9ff; }

        .product-name { font-weight: 600; color: #1e293b; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .badge { display: inline-block; padding: 3px 8px; border-radius: 6px; font-size: 10px; font-weight: 500; }
        .badge-category { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }
        .badge-color { background: #fef3c7; color: #92400e; border: 1px solid #fde68a; }
        .badge-size { background: #ede9fe; color: #5b21b6; border: 1px solid #c4b5fd; }
        .badge-location { background: #fee2e2; color: #991b1b; border: 1px solid #fecaca; }

        .price-buy { color: #dc2626; font-weight: 600; }
        .price-sell { color: #059669; font-weight: 600; }
        .quantity { font-weight: 700; color: #0369a1; }
        .quantity.low { color: #dc2626; }

        .footer { margin-top: 30px; padding-top: 15px; border-top: 2px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; font-size: 10px; color: #94a3b8; }
        .footer-left { }
        .footer-right { text-align: right; }

        .no-data { text-align: center; padding: 60px 20px; color: #94a3b8; }
        .no-data-icon { font-size: 48px; margin-bottom: 15px; opacity: 0.5; }
        .no-data-text { font-size: 16px; font-weight: 600; }

        @media print {
          body { padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none; }
          thead { background: #0ea5e9 !important; }
          .summary-item { background: #f0f9ff !important; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="header-left">
          ${options.companyLogo ? `<img src="${options.companyLogo}" alt="Logo" class="logo">` : ''}
          <div class="company-name">${options.companyName || 'Inventory Management System'}</div>
          ${options.companyAddress ? `<div class="company-details">${options.companyAddress}</div>` : ''}
          ${options.companyPhone ? `<div class="company-details">Tel: ${options.companyPhone}</div>` : ''}
          ${options.companyEmail ? `<div class="company-details">${options.companyEmail}</div>` : ''}
        </div>
        <div class="header-right">
          <div class="report-title">Inventory Report</div>
          <div class="report-subtitle">Generated: ${new Date().toLocaleString()}</div>
        </div>
      </div>

      <div class="summary-bar">
        <div class="summary-item">
          <div class="summary-label">Total Products</div>
          <div class="summary-value">${items.length}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Total Quantity</div>
          <div class="summary-value">${totalQuantity.toLocaleString()}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Total Investment</div>
          <div class="summary-value price-buy">${totalBuyValue.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Total Sell Value</div>
          <div class="summary-value price-sell">${totalSellValue.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Potential Profit</div>
          <div class="summary-value profit">${(totalSellValue - totalBuyValue).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</div>
        </div>
      </div>

      <div class="table-container">
        ${items.length === 0 ? `
          <div class="no-data">
            <div class="no-data-icon">📦</div>
            <div class="no-data-text">No inventory items found</div>
          </div>
        ` : `
          <table>
            <thead>
              <tr>
                <th style="width: 18%;">Name</th>
                <th style="width: 10%;">Reference</th>
                <th style="width: 9%;">Color</th>
                <th style="width: 7%;">Size</th>
                <th style="width: 10%;">Category</th>
                <th style="width: 7%;">Qty</th>
                <th style="width: 10%;">Buy Price</th>
                <th style="width: 10%;">Sell Price</th>
                <th style="width: 9%;">Location</th>
              </tr>
            </thead>
            <tbody>
              ${items.map((item, idx) => `
                <tr>
                  <td><div class="product-name">${item.name || '-'}</div></td>
                  <td style="color:#64748b">${item.reference_code || '-'}</td>
                  <td>${item.color_name ? `<span class="badge badge-color">${item.color_name}</span>` : '<span style="color:#94a3b8">-</span>'}</td>
                  <td>${item.size_name ? `<span class="badge badge-size">${item.size_name}</span>` : '<span style="color:#94a3b8">-</span>'}</td>
                  <td>${item.category ? `<span class="badge badge-category">${item.category}</span>` : '<span style="color:#94a3b8">-</span>'}</td>
                  <td class="quantity ${item.quantity <= 10 ? 'low' : ''}">${item.quantity}</td>
                  <td class="price-buy">${(item.buy_price || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</td>
                  <td class="price-sell">${(item.sell_price || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</td>
                  <td>${item.location ? `<span class="badge badge-location">${item.location}</span>` : '<span style="color:#94a3b8">-</span>'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `}
      </div>

      <div class="footer">
        <div class="footer-left">
          <strong>Total: ${items.length} products</strong> | Showing all inventory items
        </div>
        <div class="footer-right">
          This report is confidential and for internal use only.<br>
          Generated by Inventory Management System
        </div>
      </div>

      <script>
        window.onload = () => {
          setTimeout(() => {
            window.print();
            window.onafterprint = () => window.close();
          }, 300);
        };
      </script>
    </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
}

export function exportInvoicesToPDF(invoices: any[], options: ExportOptions): void {
  const totalRevenue = invoices.reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0);
  const pendingPayments = invoices.filter(inv => inv.payment_status === 'unpaid' || inv.payment_status === 'pending').length;
  const avgInvoiceValue = invoices.length > 0 ? totalRevenue / invoices.length : 0;
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Sales Invoices Report</title>
      <style>
        @page { size: A4; margin: 20mm; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; padding: 40px; color: #333; line-height: 1.6; }
        .header { text-align: center; margin-bottom: 40px; border-bottom: 3px solid #2563eb; padding-bottom: 20px; }
        .logo { max-width: 120px; margin-bottom: 15px; }
        .company-name { font-size: 28px; font-weight: bold; color: #1e40af; margin-bottom: 5px; }
        .company-details { font-size: 12px; color: #666; margin: 3px 0; }
        .report-title { font-size: 24px; font-weight: bold; margin: 20px 0 10px 0; color: #1e40af; }
        .generated-date { font-size: 11px; color: #666; }

        .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin: 30px 0; }
        .stat-card { background: #f8fafc; border: 2px solid #e2e8f0; border-radius: 8px; padding: 20px; text-align: center; }
        .stat-label { font-size: 12px; color: #64748b; text-transform: uppercase; font-weight: 600; margin-bottom: 8px; }
        .stat-value { font-size: 28px; font-weight: bold; color: #1e293b; }

        .empty-state { text-align: center; padding: 60px 20px; color: #94a3b8; }
        .empty-icon { font-size: 64px; margin-bottom: 20px; opacity: 0.5; }
        .empty-text { font-size: 18px; font-weight: 600; margin-bottom: 10px; }
        .empty-subtext { font-size: 13px; }

        .footer { margin-top: 50px; padding-top: 20px; border-top: 2px solid #e2e8f0; text-align: center; font-size: 11px; color: #666; }

        @media print {
          body { padding: 0; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        ${options.companyLogo ? `<img src="${options.companyLogo}" alt="Logo" class="logo">` : ''}
        <div class="company-name">${options.companyName || 'Inventory Management System'}</div>
        ${options.companyAddress ? `<div class="company-details">${options.companyAddress}</div>` : ''}
        ${options.companyPhone ? `<div class="company-details">Tel: ${options.companyPhone}</div>` : ''}
        ${options.companyEmail ? `<div class="company-details">Email: ${options.companyEmail}</div>` : ''}
        <div class="report-title">Sales Invoices Report</div>
        <div class="generated-date">Generated: ${new Date().toLocaleString()}</div>
      </div>

      <div class="summary-grid">
        <div class="stat-card">
          <div class="stat-label">Total Invoices</div>
          <div class="stat-value">${invoices.length}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Total Revenue</div>
          <div class="stat-value" style="color: #16a34a;">€${totalRevenue.toFixed(2)}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Pending</div>
          <div class="stat-value" style="color: #ca8a04;">${pendingPayments}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Avg Value</div>
          <div class="stat-value">€${avgInvoiceValue.toFixed(2)}</div>
        </div>
      </div>

      ${invoices.length === 0 ? `
        <div class="empty-state">
          <div class="empty-icon">📄</div>
          <div class="empty-text">No Invoices Found</div>
          <div class="empty-subtext">Invoices will appear here after processing sales through the Payment Terminal</div>
        </div>
      ` : `
        <table style="width: 100%; border-collapse: collapse; margin: 30px 0; font-size: 11px;">
          <thead>
            <tr style="background: #f1f5f9; border-bottom: 2px solid #cbd5e1;">
              <th style="padding: 12px; text-align: left; font-weight: 600; color: #475569;">Date</th>
              <th style="padding: 12px; text-align: left; font-weight: 600; color: #475569;">Invoice #</th>
              <th style="padding: 12px; text-align: left; font-weight: 600; color: #475569;">Customer</th>
              <th style="padding: 12px; text-align: right; font-weight: 600; color: #475569;">Items</th>
              <th style="padding: 12px; text-align: right; font-weight: 600; color: #475569;">Amount</th>
              <th style="padding: 12px; text-align: center; font-weight: 600; color: #475569;">Status</th>
            </tr>
          </thead>
          <tbody>
            ${invoices.map((invoice, idx) => `
              <tr style="border-bottom: 1px solid #e2e8f0; ${idx % 2 === 0 ? 'background: #f8fafc;' : ''}">
                <td style="padding: 10px;">${new Date(invoice.invoice_date).toLocaleDateString()}</td>
                <td style="padding: 10px; font-weight: 600;">${invoice.invoice_number}</td>
                <td style="padding: 10px;">${invoice.customer_name}</td>
                <td style="padding: 10px; text-align: right;">${invoice.invoice_items?.length || 0}</td>
                <td style="padding: 10px; text-align: right; font-weight: 600;">€${parseFloat(invoice.total_amount || 0).toFixed(2)}</td>
                <td style="padding: 10px; text-align: center;">
                  <span style="padding: 4px 12px; border-radius: 12px; font-size: 10px; font-weight: 600;
                    ${invoice.payment_status === 'paid' ? 'background: #dcfce7; color: #166534;' :
                      invoice.payment_status === 'pending' ? 'background: #fef9c3; color: #854d0e;' :
                      'background: #fee2e2; color: #991b1b;'}">
                    ${invoice.payment_status?.toUpperCase()}
                  </span>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `}

      <div class="footer">
        <p>This report is generated automatically by the Inventory Management System</p>
        <p>Confidential - For Internal Use Only</p>
      </div>

      <script>
        window.onload = () => {
          setTimeout(() => {
            window.print();
            window.onafterprint = () => window.close();
          }, 250);
        };
      </script>
    </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
}

export function exportMissingOrdersAnalysisToPDF(
  rows: MissingOrderAnalysisAggregatedRow[],
  options: ExportOptions
): void {
  const distinctOrders = rows.length;
  const totalMissingLines = rows.reduce((sum, r) => sum + r.total_missing_quantity, 0);
  const sumMissingLineTotals = rows.reduce((sum, r) => sum + r.total_to_refund, 0);
  const currency = rows[0]?.currency || 'EUR';

  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const formatOrderDate = (d: string | null | undefined) => (d ? new Date(d).toLocaleDateString() : '-');

  const tableRows =
    rows.length === 0
      ? '<tr><td colspan="12" style="text-align: center; padding: 40px; color: #64748b;">No orders with missing items</td></tr>'
      : rows
          .map(
            (r, idx) => `
            <tr style="border-bottom: 1px solid #e2e8f0; ${idx % 2 === 0 ? 'background: #f8fafc;' : ''}">
              <td style="padding: 8px; font-size: 11px;">${r.order_number ?? '-'}</td>
              <td style="padding: 8px; font-size: 11px;">${formatOrderDate(r.order_date)}</td>
              <td style="padding: 8px; font-size: 11px;">${r.customer_name ?? '-'}</td>
              <td style="padding: 8px; font-size: 11px;">${r.customer_phone ?? '-'}</td>
              <td style="padding: 8px; font-size: 11px;">${r.customer_email ?? '-'}</td>
              <td style="padding: 8px; font-size: 10px; max-width: 140px;">${r.shipping_address ?? '-'}</td>
              <td style="padding: 8px; font-size: 11px; text-align: center;">${r.missing_count}</td>
              <td style="padding: 8px; font-size: 11px;">${r.missing_skus}</td>
              <td style="padding: 8px; font-size: 11px; max-width: 120px;">${r.missing_titles}</td>
              <td style="padding: 8px; font-size: 11px; text-align: right;">${r.total_missing_quantity}</td>
              <td style="padding: 8px; font-size: 11px; text-align: right;">${Number(r.total_to_refund).toFixed(2)}</td>
              <td style="padding: 8px; font-size: 11px; text-align: right; font-weight: 600;">${Number(r.total_order_price).toFixed(2)} ${r.currency ?? ''}</td>
            </tr>
          `
          )
          .join('');

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Missing Order Analysis</title>
      <style>
        @page { size: A4; margin: 15mm; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; padding: 24px; color: #333; line-height: 1.4; font-size: 11px; }
        .header { text-align: center; margin-bottom: 24px; border-bottom: 3px solid #dc2626; padding-bottom: 16px; }
        .logo { max-width: 100px; margin-bottom: 10px; }
        .company-name { font-size: 22px; font-weight: bold; color: #1e293b; margin-bottom: 4px; }
        .company-details { font-size: 11px; color: #666; margin: 2px 0; }
        .report-title { font-size: 20px; font-weight: bold; margin: 12px 0 4px 0; color: #dc2626; }
        .generated-date { font-size: 10px; color: #64748b; }
        .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin: 20px 0; }
        .stat-card { background: #fef2f2; border: 2px solid #fecaca; border-radius: 8px; padding: 14px; text-align: center; }
        .stat-label { font-size: 10px; color: #b91c1c; text-transform: uppercase; font-weight: 600; margin-bottom: 4px; }
        .stat-value { font-size: 22px; font-weight: bold; color: #991b1b; }
        table { width: 100%; border-collapse: collapse; margin: 16px 0; font-size: 10px; }
        thead { background: #dc2626; color: white; }
        th { padding: 10px 8px; text-align: left; font-weight: 600; font-size: 9px; text-transform: uppercase; }
        th:nth-child(7), th:nth-child(10), th:nth-child(11), th:nth-child(12) { text-align: right; }
        td { padding: 8px; border-bottom: 1px solid #e2e8f0; }
        .footer { margin-top: 24px; padding-top: 12px; border-top: 2px solid #e2e8f0; text-align: center; font-size: 10px; color: #64748b; }
        @media print { body { padding: 0; } thead { background: #dc2626 !important; } }
      </style>
    </head>
    <body>
      <div class="header">
        ${options.companyLogo ? `<img src="${options.companyLogo}" alt="Logo" class="logo">` : ''}
        <div class="company-name">${options.companyName || 'Inventory Management System'}</div>
        ${options.companyAddress ? `<div class="company-details">${options.companyAddress}</div>` : ''}
        ${options.companyPhone ? `<div class="company-details">Tel: ${options.companyPhone}</div>` : ''}
        ${options.companyEmail ? `<div class="company-details">Email: ${options.companyEmail}</div>` : ''}
        <div class="report-title">Missing Order Analysis</div>
        <div class="generated-date">Generated: ${new Date().toLocaleString()}</div>
      </div>
      <div class="summary-grid">
        <div class="stat-card">
          <div class="stat-label">Orders with missing items</div>
          <div class="stat-value">${distinctOrders}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Total missing line items</div>
          <div class="stat-value">${totalMissingLines}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Total to be refunded</div>
          <div class="stat-value">${sumMissingLineTotals.toFixed(2)} ${currency}</div>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Order #</th>
            <th>Order date</th>
            <th>Customer</th>
            <th>Phone</th>
            <th>Email</th>
            <th>Address</th>
            <th># Missing</th>
            <th>Missing SKUs</th>
            <th>Missing items</th>
            <th>Total missing qty</th>
            <th>Total to refund</th>
            <th>Order total</th>
          </tr>
        </thead>
        <tbody>${tableRows}</tbody>
      </table>
      <div class="footer">
        <p>This report is generated automatically. Confidential – for internal use only.</p>
      </div>
      <script>
        window.onload = function() {
          setTimeout(function() {
            window.print();
            window.onafterprint = function() { window.close(); };
          }, 250);
        };
      </script>
    </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
}

export async function exportMissingOrdersAnalysisToExcel(
  rows: MissingOrderAnalysisAggregatedRow[],
  options: ExportOptions
): Promise<void> {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();

  workbook.creator = options.companyName || 'Inventory Management System';
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet('Missing Order Analysis', {
    properties: { tabColor: { argb: 'DC2626' } },
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1 }
  });

  worksheet.columns = [
    { key: 'order_number', width: 14 },
    { key: 'order_date', width: 12 },
    { key: 'customer_name', width: 20 },
    { key: 'customer_phone', width: 16 },
    { key: 'customer_email', width: 24 },
    { key: 'shipping_address', width: 28 },
    { key: 'missing_count', width: 10 },
    { key: 'missing_skus', width: 20 },
    { key: 'missing_titles', width: 28 },
    { key: 'total_missing_quantity', width: 14 },
    { key: 'total_to_refund', width: 14 },
    { key: 'total_order_price', width: 14 }
  ];

  const titleRow = worksheet.addRow([options.companyName || 'Inventory Management System']);
  worksheet.mergeCells('A1:L1');
  titleRow.height = 28;
  titleRow.getCell(1).font = { name: 'Arial', size: 18, bold: true, color: { argb: '7F1D1D' } };
  titleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
  titleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF2F2' } };

  const subtitleRow = worksheet.addRow(['Missing Order Analysis']);
  worksheet.mergeCells('A2:L2');
  subtitleRow.height = 22;
  subtitleRow.getCell(1).font = { name: 'Arial', size: 14, bold: true, color: { argb: 'DC2626' } };
  subtitleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
  subtitleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEE2E2' } };

  const infoRow = worksheet.addRow([`Generated: ${new Date().toLocaleString()}`]);
  worksheet.mergeCells('A3:L3');
  infoRow.getCell(1).font = { name: 'Arial', size: 10, italic: true, color: { argb: '64748B' } };
  infoRow.getCell(1).alignment = { horizontal: 'center' };

  worksheet.addRow([]);

  const headerRow = worksheet.addRow([
    'Order #',
    'Order date',
    'Customer',
    'Phone',
    'Email',
    'Address',
    '# Missing',
    'Missing SKUs',
    'Missing items',
    'Total missing qty',
    'Total to refund',
    'Order total'
  ]);
  headerRow.height = 24;
  headerRow.eachCell((cell) => {
    cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DC2626' } };
    cell.alignment = { vertical: 'middle' };
    cell.border = {
      top: { style: 'thin', color: { argb: 'B91C1C' } },
      bottom: { style: 'thin', color: { argb: 'B91C1C' } },
      left: { style: 'thin', color: { argb: 'B91C1C' } },
      right: { style: 'thin', color: { argb: 'B91C1C' } }
    };
  });
  headerRow.getCell(7).alignment = { horizontal: 'right', vertical: 'middle' };
  headerRow.getCell(10).alignment = { horizontal: 'right', vertical: 'middle' };
  headerRow.getCell(11).alignment = { horizontal: 'right', vertical: 'middle' };
  headerRow.getCell(12).alignment = { horizontal: 'right', vertical: 'middle' };

  const formatOrderDate = (d: string | null | undefined) => (d ? new Date(d).toLocaleDateString() : '');

  rows.forEach((r, index) => {
    const row = worksheet.addRow([
      r.order_number ?? '',
      formatOrderDate(r.order_date),
      r.customer_name ?? '',
      r.customer_phone ?? '',
      r.customer_email ?? '',
      r.shipping_address ?? '',
      r.missing_count,
      r.missing_skus,
      r.missing_titles,
      r.total_missing_quantity,
      Number(r.total_to_refund),
      Number(r.total_order_price)
    ]);
    row.height = 20;
    const isEven = index % 2 === 0;
    row.eachCell((cell, colNumber) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isEven ? 'FFFFFF' : 'F8FAFC' } };
      cell.border = { bottom: { style: 'thin', color: { argb: 'E2E8F0' } } };
      cell.alignment = { vertical: 'middle' };
      if (colNumber === 7 || colNumber === 10) {
        cell.alignment = { horizontal: 'right', vertical: 'middle' };
      }
      if (colNumber === 11 || colNumber === 12) {
        cell.numFmt = '#,##0.00';
        cell.alignment = { horizontal: 'right', vertical: 'middle' };
      }
    });
  });

  const totalToRefund = rows.reduce((sum, r) => sum + r.total_to_refund, 0);
  const totalRow = worksheet.addRow([
    '', '', '', '', '', '', '', '', '',
    'Total to be refunded:',
    totalToRefund,
    ''
  ]);
  totalRow.height = 22;
  totalRow.eachCell((cell, colNumber) => {
    cell.font = { name: 'Arial', size: 10, bold: true };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF2F2' } };
    if (colNumber === 10) {
      cell.alignment = { horizontal: 'right', vertical: 'middle' };
    }
    if (colNumber === 11) {
      cell.numFmt = '#,##0.00';
      cell.alignment = { horizontal: 'right', vertical: 'middle' };
    }
  });

  worksheet.views = [{ state: 'frozen', ySplit: 5, activeCell: 'A6' }];

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `missing_order_analysis_${new Date().toISOString().split('T')[0]}.xlsx`;
  link.click();
  window.URL.revokeObjectURL(url);
}

function colToLetter(colIndex: number): string {
  let result = '';
  let n = colIndex;
  while (n > 0) {
    const rem = (n - 1) % 26;
    result = String.fromCharCode(65 + rem) + result;
    n = Math.floor((n - 1) / 26);
  }
  return result;
}

export async function exportInventoryMatrixToExcel(data: MatrixExportData, options: ExportOptions): Promise<void> {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();

  workbook.creator = options.companyName || 'Inventory Management System';
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet('Inventory Matrix Report', {
    properties: { tabColor: { argb: '0EA5E9' } },
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1 }
  });

  const baseColumns = [
    { key: 'photo', width: 7 },
    { key: 'name', width: 34 },
    { key: 'sku', width: 16 },
    { key: 'reference', width: 16 },
    { key: 'color', width: 14 },
    { key: 'quantity', width: 10 },
    { key: 'sell_price', width: 12 },
    { key: 'buy_price', width: 12 },
    { key: 'location', width: 20 },
    { key: 'bin_location', width: 22 }
  ];

  const sizeColumnDefs = data.sizeColumns.map(size => ({
    key: `size_${size}`,
    width: Math.max(7, size.length + 2)
  }));

  worksheet.columns = [...baseColumns, ...sizeColumnDefs];

  const totalCols = baseColumns.length + sizeColumnDefs.length;
  const lastColLetter = colToLetter(totalCols);

  const titleRow = worksheet.addRow([options.companyName || 'Inventory Management System']);
  worksheet.mergeCells(`A1:${lastColLetter}1`);
  titleRow.height = 35;
  titleRow.getCell(1).font = { name: 'Arial', size: 20, bold: true, color: { argb: '0C4A6E' } };
  titleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
  titleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F0F9FF' } };

  const subtitleRow = worksheet.addRow(['INVENTORY MATRIX REPORT']);
  worksheet.mergeCells(`A2:${lastColLetter}2`);
  subtitleRow.height = 25;
  subtitleRow.getCell(1).font = { name: 'Arial', size: 14, bold: true, color: { argb: '0369A1' } };
  subtitleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
  subtitleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E0F2FE' } };

  const infoRow = worksheet.addRow([`Generated: ${new Date().toLocaleString()}`]);
  worksheet.mergeCells(`A3:${lastColLetter}3`);
  infoRow.getCell(1).font = { name: 'Arial', size: 10, italic: true, color: { argb: '64748B' } };
  infoRow.getCell(1).alignment = { horizontal: 'center' };

  worksheet.addRow([]);

  const summaryTitleRow = worksheet.addRow(['SUMMARY']);
  worksheet.mergeCells(`A5:${lastColLetter}5`);
  summaryTitleRow.height = 25;
  summaryTitleRow.getCell(1).font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FFFFFF' } };
  summaryTitleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0EA5E9' } };
  summaryTitleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };

  const summaryData = [
    ['Unique Products', data.stats.uniqueProducts, '', 'Color Variants', data.stats.totalVariants, '', 'Total Qty', data.stats.totalQty],
    ['Total Buy Value', data.stats.buyValue, '', 'Total Sell Value', data.stats.sellValue, '', 'Profit', data.stats.sellValue - data.stats.buyValue]
  ];

  summaryData.forEach((rowData) => {
    const row = worksheet.addRow(rowData);
    row.height = 22;

    [1, 4, 7].forEach(col => {
      if (row.getCell(col).value) {
        row.getCell(col).font = { name: 'Arial', size: 10, bold: true, color: { argb: '475569' } };
        row.getCell(col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F8FAFC' } };
      }
    });

    [2, 5, 8].forEach(col => {
      const cell = row.getCell(col);
      if (cell.value !== '' && cell.value !== undefined) {
        cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: '0C4A6E' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F0F9FF' } };
        if (typeof cell.value === 'number' && col !== 2 && col !== 5) {
          if (col === 8) {
            cell.numFmt = '"EUR "#,##0.00';
            cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: '059669' } };
          }
        }
      }
    });
  });

  worksheet.addRow([]);
  worksheet.addRow([]);

  const headerLabels = ['', 'Product Name', 'SKU', 'Reference', 'Color', 'Qty', 'Sell Price', 'Buy Price', 'Location', 'Bin Location', ...data.sizeColumns];
  const headerRow = worksheet.addRow(headerLabels);
  headerRow.height = 28;
  headerRow.eachCell((cell, colNumber) => {
    cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0284C7' } };
    cell.alignment = { horizontal: colNumber >= 6 ? 'center' : 'left', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin', color: { argb: '0369A1' } },
      bottom: { style: 'thin', color: { argb: '0369A1' } },
      left: { style: 'thin', color: { argb: '0369A1' } },
      right: { style: 'thin', color: { argb: '0369A1' } }
    };
  });

  const imageMap = new Map<string, number>();
  for (const group of data.groups) {
    if (!group.imageUrl) continue;
    try {
      const resp = await fetch(group.imageUrl);
      if (!resp.ok) continue;
      const buf = await resp.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      const base64 = btoa(binary);
      const ext = group.imageUrl.toLowerCase().includes('.png') ? 'png' : 'jpeg';
      const imageId = workbook.addImage({ base64, extension: ext });
      imageMap.set(group.productName, imageId);
    } catch {
    }
  }

  let nextRow0Idx = worksheet.rowCount;

  data.groups.forEach((group, groupIdx) => {
    const groupFirstRow0Idx = nextRow0Idx;

    group.colors.forEach((colorVariant, colorIdx) => {
      const isFirstRow = colorIdx === 0;
      const rowData = [
        '',
        isFirstRow ? group.productName : '',
        isFirstRow ? group.sku : '',
        isFirstRow ? group.referenceCode : '',
        colorVariant.colorName,
        colorVariant.totalQty,
        isFirstRow ? group.sellPrice : '',
        isFirstRow ? group.buyPrice : '',
        isFirstRow ? group.location : '',
        isFirstRow ? (group.binLocation ? formatBinLocationCompact(group.binLocation) : 'No bin location') : '',
        ...data.sizeColumns.map(size => colorVariant.sizeQuantities[size] || '')
      ];

      const row = worksheet.addRow(rowData);
      row.height = isFirstRow ? 42 : 20;
      nextRow0Idx++;
      const isEvenGroup = groupIdx % 2 === 0;
      const bgColor = isEvenGroup ? 'FFFFFF' : 'F8FAFC';

      row.eachCell((cell, colNumber) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
        cell.border = {
          bottom: { style: 'thin', color: { argb: 'E2E8F0' } }
        };
        cell.alignment = { vertical: 'middle' };

        if (colNumber === 1) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
        } else if (colNumber === 2 && isFirstRow) {
          cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: '1E293B' } };
          cell.alignment = { vertical: 'middle', indent: 1 };
        } else if (colNumber === 3 && isFirstRow) {
          cell.font = { name: 'Courier New', size: 9, color: { argb: '64748B' } };
        } else if (colNumber === 4) {
          cell.font = { name: 'Arial', size: 10, color: { argb: '64748B' } };
        } else if (colNumber === 5) {
          cell.font = { name: 'Arial', size: 10, color: { argb: '92400E' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF3C7' } };
        } else if (colNumber === 6) {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          const isLowStock = colorVariant.totalQty <= 10;
          cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: isLowStock ? 'DC2626' : '0369A1' } };
          if (isLowStock) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEE2E2' } };
          }
        } else if (colNumber === 7 && isFirstRow) {
          cell.numFmt = '"EUR "#,##0.00';
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
          cell.font = { name: 'Arial', size: 10, color: { argb: '059669' } };
        } else if (colNumber === 8 && isFirstRow) {
          cell.numFmt = '"EUR "#,##0.00';
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
          cell.font = { name: 'Arial', size: 10, color: { argb: 'DC2626' } };
        } else if (colNumber === 9 && isFirstRow && group.location) {
          cell.font = { name: 'Arial', size: 10, color: { argb: '991B1B' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEE2E2' } };
        } else if (colNumber === 10 && isFirstRow) {
          if (group.binLocation) {
            cell.font = { name: 'Arial', size: 9, color: { argb: '047857' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'ECFDF5' } };
          } else {
            cell.font = { name: 'Arial', size: 9, color: { argb: 'DC2626' } };
          }
        } else if (colNumber > 10) {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          if (cell.value) {
            const qty = cell.value as number;
            cell.font = { name: 'Arial', size: 10, bold: qty <= 5, color: { argb: qty <= 5 ? 'DC2626' : '374151' } };
          } else {
            cell.font = { name: 'Arial', size: 10, color: { argb: 'CBD5E1' } };
          }
        } else {
          cell.font = { name: 'Arial', size: 10, color: { argb: '475569' } };
        }
      });
    });

    if (imageMap.has(group.productName)) {
      worksheet.addImage(imageMap.get(group.productName)!, {
        tl: { col: 0.1, row: groupFirstRow0Idx + 0.1 },
        ext: { width: 44, height: 38 }
      } as never);
    }

    if (groupIdx < data.groups.length - 1) {
      const lastRowNum = worksheet.rowCount;
      const lastRow = worksheet.getRow(lastRowNum);
      lastRow.eachCell((cell) => {
        cell.border = {
          ...cell.border,
          bottom: { style: 'medium', color: { argb: '94A3B8' } }
        };
      });
    }
  });

  worksheet.addRow([]);
  const totalRowData = ['', 'TOTALS', '', '', '', data.stats.totalQty, '', '', '', '', ...data.sizeColumns.map(() => '')];
  const totalRow = worksheet.addRow(totalRowData);
  totalRow.height = 28;
  totalRow.eachCell((cell, colNumber) => {
    cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0C4A6E' } };
    cell.alignment = { horizontal: colNumber >= 5 ? 'center' : 'left', vertical: 'middle' };
  });

  worksheet.addRow([]);
  worksheet.addRow([]);
  const footerRow = worksheet.addRow(['This report is confidential and for internal use only.']);
  worksheet.mergeCells(`A${footerRow.number}:${lastColLetter}${footerRow.number}`);
  footerRow.getCell(1).font = { name: 'Arial', size: 9, italic: true, color: { argb: '94A3B8' } };
  footerRow.getCell(1).alignment = { horizontal: 'center' };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `inventory_matrix_report_${new Date().toISOString().split('T')[0]}.xlsx`;
  link.click();
  window.URL.revokeObjectURL(url);
}

export function exportInventoryMatrixToPDF(data: MatrixExportData, options: ExportOptions): void {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const sizeHeaders = data.sizeColumns.map(size => `<th style="padding: 8px 4px; text-align: center; font-size: 9px; min-width: 40px;">${size}</th>`).join('');

  let tableRows = '';
  data.groups.forEach((group, groupIdx) => {
    group.colors.forEach((colorVariant, colorIdx) => {
      const isFirstRow = colorIdx === 0;
      const isLastRow = colorIdx === group.colors.length - 1;
      const bgColor = groupIdx % 2 === 0 ? '#ffffff' : '#f8fafc';
      const borderBottom = isLastRow && groupIdx < data.groups.length - 1 ? '2px solid #94a3b8' : '1px solid #e2e8f0';

      const sizeCells = data.sizeColumns.map(size => {
        const qty = colorVariant.sizeQuantities[size];
        if (qty) {
          const color = qty <= 5 ? '#dc2626' : '#374151';
          const fontWeight = qty <= 5 ? '600' : '400';
          return `<td style="padding: 6px 4px; text-align: center; color: ${color}; font-weight: ${fontWeight}; font-size: 10px;">${qty}</td>`;
        }
        return `<td style="padding: 6px 4px; text-align: center; color: #cbd5e1; font-size: 10px;">-</td>`;
      }).join('');

      const productCell = isFirstRow
        ? `<div style="display: flex; align-items: center; gap: 6px;">
            ${group.imageUrl ? `<img src="${group.imageUrl}" style="width: 28px; height: 28px; object-fit: cover; border-radius: 4px; border: 1px solid #e2e8f0; flex-shrink: 0;" onerror="this.style.display='none'">` : ''}
            <div style="min-width: 0;">
              <div style="font-weight: 600; color: #1e293b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 140px;">${group.productName}</div>
              ${group.sku ? `<div style="font-size: 9px; font-family: monospace; color: #64748b; margin-top: 1px;">${group.sku}</div>` : ''}
            </div>
          </div>`
        : '<span style="color: #cbd5e1;">-</span>';

      tableRows += `
        <tr style="background: ${bgColor}; border-bottom: ${borderBottom};">
          <td style="padding: 6px 8px; max-width: 180px;">
            ${productCell}
          </td>
          <td style="padding: 6px 8px; color: ${isFirstRow ? '#64748b' : '#cbd5e1'}; font-size: 11px;">
            ${isFirstRow ? (group.referenceCode || '-') : '-'}
          </td>
          <td style="padding: 6px 8px;">
            <span style="display: inline-flex; align-items: center; gap: 4px;">
              ${colorVariant.hexCode ? `<span style="width: 12px; height: 12px; border-radius: 50%; background: ${colorVariant.hexCode}; border: 1px solid #d1d5db;"></span>` : ''}
              <span style="background: #fef3c7; color: #92400e; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 500;">${colorVariant.colorName}</span>
            </span>
          </td>
          <td style="padding: 6px 8px; text-align: center; font-weight: 600; color: ${colorVariant.totalQty <= 10 ? '#dc2626' : '#0369a1'};">
            ${colorVariant.totalQty}
          </td>
          <td style="padding: 6px 8px; text-align: right; color: ${isFirstRow ? '#059669' : '#cbd5e1'}; font-weight: 500; font-size: 11px;">
            ${isFirstRow ? group.sellPrice.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : '-'}
          </td>
          <td style="padding: 6px 8px; text-align: right; color: ${isFirstRow ? '#dc2626' : '#cbd5e1'}; font-weight: 500; font-size: 11px;">
            ${isFirstRow ? group.buyPrice.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : '-'}
          </td>
          <td style="padding: 6px 8px; font-size: 11px;">
            ${isFirstRow && group.location ? `<span style="background: #fee2e2; color: #991b1b; padding: 2px 6px; border-radius: 4px; font-size: 10px;">${group.location}</span>` : '<span style="color: #cbd5e1;">-</span>'}
          </td>
          <td style="padding: 6px 8px; font-size: 10px;">
            ${isFirstRow ? (group.binLocation ? `<span style="background: #ecfdf5; color: #047857; padding: 2px 6px; border-radius: 4px; font-size: 9px;">${formatBinLocationCompact(group.binLocation)}</span>` : '<span style="color: #dc2626; font-size: 9px;">No bin location</span>') : '<span style="color: #cbd5e1;">-</span>'}
          </td>
          ${sizeCells}
        </tr>
      `;
    });
  });

  const matrixHtml = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Inventory Matrix Report</title>
      <style>
        @page { size: A4 landscape; margin: 12mm; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; color: #1e293b; line-height: 1.4; background: #fff; font-size: 11px; }

        .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 3px solid #0ea5e9; }
        .header-left { flex: 1; }
        .logo { max-width: 80px; margin-bottom: 8px; }
        .company-name { font-size: 20px; font-weight: 700; color: #0369a1; margin-bottom: 4px; }
        .company-details { font-size: 10px; color: #64748b; margin: 2px 0; }
        .header-right { text-align: right; }
        .report-title { font-size: 22px; font-weight: 700; color: #0c4a6e; margin-bottom: 4px; }
        .report-subtitle { font-size: 10px; color: #64748b; }

        .summary-bar { display: flex; gap: 10px; margin-bottom: 20px; flex-wrap: wrap; }
        .summary-item { flex: 1; min-width: 100px; background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); border: 1px solid #bae6fd; border-radius: 8px; padding: 10px 12px; text-align: center; }
        .summary-label { font-size: 9px; color: #0369a1; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px; margin-bottom: 3px; }
        .summary-value { font-size: 16px; font-weight: 700; color: #0c4a6e; }
        .summary-value.profit { color: #059669; }

        .table-container { background: #fff; border-radius: 8px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.1); border: 1px solid #e2e8f0; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        thead { background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%); }
        th { padding: 10px 8px; text-align: left; font-weight: 600; font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px; color: white; }

        .footer { margin-top: 20px; padding-top: 12px; border-top: 2px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; font-size: 9px; color: #94a3b8; }

        @media print {
          body { padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none; }
          thead { background: #0ea5e9 !important; }
          .summary-item { background: #f0f9ff !important; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="header-left">
          ${options.companyLogo ? `<img src="${options.companyLogo}" alt="Logo" class="logo">` : ''}
          <div class="company-name">${options.companyName || 'Inventory Management System'}</div>
          ${options.companyAddress ? `<div class="company-details">${options.companyAddress}</div>` : ''}
          ${options.companyPhone ? `<div class="company-details">Tel: ${options.companyPhone}</div>` : ''}
          ${options.companyEmail ? `<div class="company-details">${options.companyEmail}</div>` : ''}
        </div>
        <div class="header-right">
          <div class="report-title">Inventory Matrix Report</div>
          <div class="report-subtitle">Generated: ${new Date().toLocaleString()}</div>
        </div>
      </div>

      <div class="summary-bar">
        <div class="summary-item">
          <div class="summary-label">Unique Products</div>
          <div class="summary-value">${data.stats.uniqueProducts}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Color Variants</div>
          <div class="summary-value">${data.stats.totalVariants}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Total Quantity</div>
          <div class="summary-value">${data.stats.totalQty.toLocaleString()}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">With SKU</div>
          <div class="summary-value">${data.stats.withSku}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">With Bin</div>
          <div class="summary-value" style="color: #047857;">${data.stats.withBin}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Buy Value</div>
          <div class="summary-value" style="color: #dc2626;">${data.stats.buyValue.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Sell Value</div>
          <div class="summary-value" style="color: #059669;">${data.stats.sellValue.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Profit</div>
          <div class="summary-value profit">${(data.stats.sellValue - data.stats.buyValue).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</div>
        </div>
      </div>

      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th style="min-width: 160px;">Product</th>
              <th style="min-width: 80px;">Reference</th>
              <th style="min-width: 80px;">Color</th>
              <th style="min-width: 45px; text-align: center;">Qty</th>
              <th style="min-width: 65px; text-align: right;">Sell</th>
              <th style="min-width: 65px; text-align: right;">Buy</th>
              <th style="min-width: 60px;">Location</th>
              <th style="min-width: 90px;">Bin</th>
              ${sizeHeaders}
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </div>

      <div class="footer">
        <div>
          <strong>Total: ${data.stats.uniqueProducts} products</strong> | ${data.stats.totalVariants} variants | ${data.stats.withSku} with SKU | ${data.stats.withBin} with bin | ${data.sizeColumns.length} sizes
        </div>
        <div style="text-align: right;">
          This report is confidential and for internal use only.<br>
          Generated by Inventory Management System
        </div>
      </div>

      <script>
        window.onload = () => {
          setTimeout(() => {
            window.print();
            window.onafterprint = () => window.close();
          }, 300);
        };
      </script>
    </body>
    </html>
  `;

  printWindow.document.write(matrixHtml);
  printWindow.document.close();
}

interface ComparisonExportData {
  groups: {
    productName: string;
    referenceCode: string;
    category: string;
    imageUrl?: string;
    sellPrice: number;
    buyPrice: number;
    warehouseQty: number;
    otherQty: number;
    difference: number;
    colors: {
      colorId: string;
      colorName: string;
      hexCode?: string;
      warehouseQty: number;
      otherQty: number;
      difference: number;
      sizeBreakdown: { size: string; warehouseQty: number; otherQty: number; difference: number }[];
    }[];
  }[];
  sizeColumns: string[];
  stats: {
    totalProducts: number;
    productsWithDifference: number;
    warehouseOnlyCount: number;
    otherLocationOnlyCount: number;
    warehouseTotalQty: number;
    otherLocationTotalQty: number;
    netDifference: number;
  };
  warehouseLocation: { id: string; name: string };
  comparisonLocation: { id: string; name: string };
}

export async function exportLocationComparisonToExcel(data: ComparisonExportData, options: ExportOptions): Promise<void> {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();

  workbook.creator = options.companyName || 'Inventory Management System';
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet('Location Comparison', {
    properties: { tabColor: { argb: 'F59E0B' } },
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1 }
  });

  const baseColumns = [
    { key: 'image', width: 7 },
    { key: 'name', width: 28 },
    { key: 'reference', width: 15 },
    { key: 'color', width: 15 },
    { key: 'warehouseQty', width: 15 },
    { key: 'otherQty', width: 15 },
    { key: 'difference', width: 12 }
  ];

  const sizeColumns = data.sizeColumns.map(size => ({
    key: `size_${size}`,
    width: 14
  }));

  worksheet.columns = [...baseColumns, ...sizeColumns];

  const imageMap = new Map<string, number>();
  for (const group of data.groups) {
    if (!group.imageUrl) continue;
    try {
      const resp = await fetch(group.imageUrl);
      if (!resp.ok) continue;
      const buf = await resp.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      const base64 = btoa(binary);
      const ext = group.imageUrl.toLowerCase().includes('.png') ? 'png' : 'jpeg';
      const imageId = workbook.addImage({ base64, extension: ext });
      imageMap.set(group.productName, imageId);
    } catch {
    }
  }

  const titleRow = worksheet.addRow([options.companyName || 'Inventory Management System']);
  const colCount = 7 + data.sizeColumns.length;
  worksheet.mergeCells(`A1:${String.fromCharCode(64 + colCount)}1`);
  titleRow.height = 35;
  titleRow.getCell(1).font = { name: 'Arial', size: 20, bold: true, color: { argb: '1E3A5F' } };
  titleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
  titleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF3C7' } };

  const subtitleRow = worksheet.addRow([`LOCATION COMPARISON: ${data.warehouseLocation.name} vs ${data.comparisonLocation.name}`]);
  worksheet.mergeCells(`A2:${String.fromCharCode(64 + colCount)}2`);
  subtitleRow.height = 25;
  subtitleRow.getCell(1).font = { name: 'Arial', size: 14, bold: true, color: { argb: 'B45309' } };
  subtitleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
  subtitleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF9C3' } };

  const infoRow = worksheet.addRow([`Generated: ${new Date().toLocaleString()}`]);
  worksheet.mergeCells(`A3:${String.fromCharCode(64 + colCount)}3`);
  infoRow.getCell(1).font = { name: 'Arial', size: 10, italic: true, color: { argb: '64748B' } };
  infoRow.getCell(1).alignment = { horizontal: 'center' };

  worksheet.addRow([]);

  const summaryTitleRow = worksheet.addRow(['COMPARISON SUMMARY']);
  worksheet.mergeCells(`A5:${String.fromCharCode(64 + colCount)}5`);
  summaryTitleRow.height = 25;
  summaryTitleRow.getCell(1).font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FFFFFF' } };
  summaryTitleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F59E0B' } };
  summaryTitleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };

  const summaryData = [
    ['Total Products', data.stats.totalProducts, '', 'With Differences', data.stats.productsWithDifference, '', 'Net Difference', data.stats.netDifference],
    [`${data.warehouseLocation.name} Total`, data.stats.warehouseTotalQty, '', `${data.comparisonLocation.name} Total`, data.stats.otherLocationTotalQty, '', '', '']
  ];

  summaryData.forEach(rowData => {
    const row = worksheet.addRow(rowData);
    row.height = 22;

    [1, 4, 7].forEach(col => {
      if (row.getCell(col).value) {
        row.getCell(col).font = { name: 'Arial', size: 10, bold: true, color: { argb: '475569' } };
        row.getCell(col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F8FAFC' } };
      }
    });

    [2, 5, 8].forEach(col => {
      const cell = row.getCell(col);
      if (cell.value !== '' && cell.value !== undefined) {
        cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: '0C4A6E' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F0F9FF' } };
        if (col === 8 && typeof cell.value === 'number') {
          const diff = cell.value as number;
          if (diff > 0) {
            cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: '059669' } };
          } else if (diff < 0) {
            cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'DC2626' } };
          }
        }
      }
    });
  });

  worksheet.addRow([]);
  worksheet.addRow([]);

  const headerValues = [
    '',
    'Product Name',
    'Reference',
    'Color',
    data.warehouseLocation.name,
    data.comparisonLocation.name,
    'Difference',
    ...data.sizeColumns.map(s => s)
  ];

  const headerRow = worksheet.addRow(headerValues);
  headerRow.height = 28;
  headerRow.eachCell((cell, colNumber) => {
    cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFF' } };
    cell.alignment = { horizontal: colNumber >= 5 ? 'center' : 'left', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin', color: { argb: '334155' } },
      bottom: { style: 'thin', color: { argb: '334155' } },
      left: { style: 'thin', color: { argb: '334155' } },
      right: { style: 'thin', color: { argb: '334155' } }
    };

    if (colNumber === 5) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '3B82F6' } };
    } else if (colNumber === 6) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F59E0B' } };
    } else {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '475569' } };
    }
  });

  let rowIndex = 0;
  const headerRowNumber = worksheet.rowCount;
  data.groups.forEach(group => {
    const groupFirstRowNumber = worksheet.rowCount + 1;
    group.colors.forEach((colorVariant, colorIdx) => {
      const isFirstRow = colorIdx === 0;
      const rowData = [
        '',
        isFirstRow ? group.productName : '',
        isFirstRow ? group.referenceCode : '',
        colorVariant.colorName,
        colorVariant.warehouseQty,
        colorVariant.otherQty,
        colorVariant.difference
      ];

      data.sizeColumns.forEach(size => {
        const sizeData = colorVariant.sizeBreakdown.find(s => s.size === size);
        if (sizeData && (sizeData.warehouseQty > 0 || sizeData.otherQty > 0)) {
          rowData.push(`${sizeData.warehouseQty}/${sizeData.otherQty}` as any);
        } else {
          rowData.push('-' as any);
        }
      });

      const row = worksheet.addRow(rowData);
      row.height = 22;
      const isEven = rowIndex % 2 === 0;

      let bgColor = isEven ? 'FFFFFF' : 'F8FAFC';
      if (colorVariant.difference > 0) {
        bgColor = 'ECFDF5';
      } else if (colorVariant.difference < 0) {
        bgColor = 'FEF2F2';
      }

      row.eachCell((cell, colNumber) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
        cell.border = { bottom: { style: 'thin', color: { argb: 'E2E8F0' } } };
        cell.alignment = { vertical: 'middle' };

        if (colNumber === 2 && isFirstRow) {
          cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: '1E293B' } };
        } else if (colNumber === 3) {
          cell.font = { name: 'Arial', size: 10, color: { argb: '64748B' } };
        } else if (colNumber === 4) {
          cell.font = { name: 'Arial', size: 10, color: { argb: '92400E' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF3C7' } };
        } else if (colNumber === 5) {
          cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: '1E40AF' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DBEAFE' } };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        } else if (colNumber === 6) {
          cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'B45309' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF3C7' } };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        } else if (colNumber === 7) {
          const diff = colorVariant.difference;
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          if (diff > 0) {
            cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: '059669' } };
            cell.value = `+${diff}`;
          } else if (diff < 0) {
            cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'DC2626' } };
          } else {
            cell.font = { name: 'Arial', size: 10, color: { argb: '94A3B8' } };
          }
        } else if (colNumber > 7) {
          cell.font = { name: 'Arial', size: 9, color: { argb: '475569' } };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        }
      });

      rowIndex++;
    });

    if (imageMap.has(group.productName)) {
      const groupFirstRow0Idx = groupFirstRowNumber - 1;
      worksheet.addImage(imageMap.get(group.productName)!, {
        tl: { col: 0.1, row: groupFirstRow0Idx + 0.1 },
        ext: { width: 44, height: 38 }
      } as never);
    }
  });

  worksheet.addRow([]);
  const totalRow = worksheet.addRow([
    '',
    'TOTALS',
    '',
    '',
    data.stats.warehouseTotalQty,
    data.stats.otherLocationTotalQty,
    data.stats.netDifference
  ]);
  totalRow.height = 28;
  totalRow.eachCell((cell, colNumber) => {
    cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1E293B' } };
    cell.alignment = { horizontal: colNumber >= 5 ? 'center' : 'left', vertical: 'middle' };
    if (colNumber === 7) {
      const diff = data.stats.netDifference;
      if (diff > 0) {
        cell.value = `+${diff}`;
        cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: '34D399' } };
      } else if (diff < 0) {
        cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'F87171' } };
      }
    }
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `location_comparison_${data.warehouseLocation.name}_vs_${data.comparisonLocation.name}_${new Date().toISOString().split('T')[0]}.xlsx`;
  link.click();
  window.URL.revokeObjectURL(url);
}

export function exportLocationComparisonToPDF(data: ComparisonExportData, options: ExportOptions): void {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const sizeHeaders = data.sizeColumns.map(size =>
    `<th style="min-width: 70px; text-align: center; font-size: 10px;">${size}<br><span style="color: #3B82F6; font-size: 9px;">W</span>/<span style="color: #F59E0B; font-size: 9px;">O</span></th>`
  ).join('');

  const tableRows = data.groups.map(group => {
    return group.colors.map((colorVariant, colorIdx) => {
      const isFirstRow = colorIdx === 0;
      const diffClass = colorVariant.difference > 0 ? 'positive' : colorVariant.difference < 0 ? 'negative' : 'neutral';
      const rowBg = colorVariant.difference > 0 ? '#ECFDF5' : colorVariant.difference < 0 ? '#FEF2F2' : '';

      const sizeCells = data.sizeColumns.map(size => {
        const sizeData = colorVariant.sizeBreakdown.find(s => s.size === size);
        if (sizeData && (sizeData.warehouseQty > 0 || sizeData.otherQty > 0)) {
          return `<td style="text-align: center; font-size: 10px;"><span style="color: #3B82F6;">${sizeData.warehouseQty}</span>/<span style="color: #F59E0B;">${sizeData.otherQty}</span></td>`;
        }
        return '<td style="text-align: center; color: #CBD5E1;">-</td>';
      }).join('');

      const imageCell = isFirstRow
        ? `<td style="width: 36px; padding: 4px;">${group.imageUrl ? `<img src="${group.imageUrl}" style="width: 28px; height: 28px; object-fit: cover; border-radius: 4px; border: 1px solid #e2e8f0;" onerror="this.style.display='none'">` : ''}</td>`
        : '<td></td>';

      return `
        <tr style="${rowBg ? `background: ${rowBg};` : ''}">
          ${imageCell}
          <td style="font-weight: ${isFirstRow ? '600' : '400'}; color: ${isFirstRow ? '#1E293B' : '#CBD5E1'};">${isFirstRow ? group.productName : '-'}</td>
          <td style="color: #64748B; font-size: 11px;">${isFirstRow ? (group.referenceCode || '-') : '-'}</td>
          <td><span class="color-badge">${colorVariant.colorName}</span></td>
          <td class="warehouse-cell">${colorVariant.warehouseQty}</td>
          <td class="other-cell">${colorVariant.otherQty}</td>
          <td class="diff-${diffClass}">${colorVariant.difference > 0 ? '+' : ''}${colorVariant.difference}</td>
          ${sizeCells}
        </tr>
      `;
    }).join('');
  }).join('');

  const comparisonHtml = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Location Comparison Report</title>
      <style>
        @page { size: A4 landscape; margin: 15mm; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; color: #333; line-height: 1.4; font-size: 11px; }

        .header { text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 3px solid #F59E0B; }
        .logo { max-width: 80px; margin-bottom: 10px; }
        .company-name { font-size: 22px; font-weight: bold; color: #1E3A5F; margin-bottom: 4px; }
        .report-title { font-size: 16px; font-weight: bold; color: #B45309; margin-bottom: 4px; }
        .comparison-badge { display: inline-flex; align-items: center; gap: 8px; margin-top: 8px; }
        .location-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 600; }
        .warehouse-badge { background: #DBEAFE; color: #1E40AF; }
        .other-badge { background: #FEF3C7; color: #B45309; }
        .vs-text { color: #94A3B8; font-weight: 500; }
        .generated-date { font-size: 10px; color: #64748B; margin-top: 6px; }

        .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 20px; }
        .summary-item { background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 8px; padding: 12px; text-align: center; }
        .summary-label { font-size: 10px; color: #64748B; text-transform: uppercase; font-weight: 600; margin-bottom: 4px; }
        .summary-value { font-size: 20px; font-weight: bold; color: #1E293B; }
        .summary-value.positive { color: #059669; }
        .summary-value.negative { color: #DC2626; }

        .legend { display: flex; gap: 20px; margin-bottom: 12px; justify-content: space-between; align-items: center; padding: 8px 12px; background: #F8FAFC; border-radius: 8px; }
        .legend-item { display: flex; align-items: center; gap: 6px; font-size: 10px; color: #475569; }
        .legend-dot { width: 10px; height: 10px; border-radius: 50%; }
        .legend-dot.warehouse { background: #3B82F6; }
        .legend-dot.other { background: #F59E0B; }
        .legend-dot.positive { background: #10B981; }
        .legend-dot.negative { background: #F43F5E; }

        .table-container { overflow-x: auto; border-radius: 8px; border: 1px solid #E2E8F0; }
        table { width: 100%; border-collapse: collapse; }
        thead { background: linear-gradient(135deg, #475569 0%, #334155 100%); }
        th { padding: 10px 8px; text-align: left; font-weight: 600; font-size: 10px; text-transform: uppercase; color: #FFFFFF; }
        th.warehouse-header { background: #3B82F6 !important; }
        th.other-header { background: #F59E0B !important; }
        td { padding: 8px; border-bottom: 1px solid #E2E8F0; font-size: 11px; vertical-align: middle; }
        tbody tr:hover { background: #F8FAFC; }

        .color-badge { display: inline-block; padding: 2px 8px; background: #FEF3C7; color: #92400E; border-radius: 4px; font-size: 10px; font-weight: 500; }
        .warehouse-cell { text-align: center; font-weight: 600; color: #1E40AF; background: #EFF6FF; }
        .other-cell { text-align: center; font-weight: 600; color: #B45309; background: #FFFBEB; }
        .diff-positive { text-align: center; font-weight: 600; color: #059669; }
        .diff-negative { text-align: center; font-weight: 600; color: #DC2626; }
        .diff-neutral { text-align: center; color: #94A3B8; }

        .footer { margin-top: 15px; padding-top: 10px; border-top: 1px solid #E2E8F0; display: flex; justify-content: space-between; font-size: 9px; color: #64748B; }

        @media print {
          body { padding: 0; }
          .table-container { page-break-inside: auto; }
          tr { page-break-inside: avoid; page-break-after: auto; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        ${options.companyLogo ? `<img src="${options.companyLogo}" alt="Logo" class="logo">` : ''}
        <div class="company-name">${options.companyName || 'Inventory Management System'}</div>
        <div class="report-title">Location Comparison Report</div>
        <div class="comparison-badge">
          <span class="location-badge warehouse-badge">${data.warehouseLocation.name}</span>
          <span class="vs-text">vs</span>
          <span class="location-badge other-badge">${data.comparisonLocation.name}</span>
        </div>
        <div class="generated-date">Generated: ${new Date().toLocaleString()}</div>
      </div>

      <div class="summary-grid">
        <div class="summary-item">
          <div class="summary-label">Total Products</div>
          <div class="summary-value">${data.stats.totalProducts}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">With Differences</div>
          <div class="summary-value" style="color: #D97706;">${data.stats.productsWithDifference}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">${data.warehouseLocation.name} Total</div>
          <div class="summary-value" style="color: #1E40AF;">${data.stats.warehouseTotalQty.toLocaleString()}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">${data.comparisonLocation.name} Total</div>
          <div class="summary-value" style="color: #B45309;">${data.stats.otherLocationTotalQty.toLocaleString()}</div>
        </div>
      </div>

      <div class="legend">
        <div style="display: flex; gap: 16px;">
          <div class="legend-item"><span class="legend-dot warehouse"></span> ${data.warehouseLocation.name} (Default)</div>
          <div class="legend-item"><span class="legend-dot other"></span> ${data.comparisonLocation.name}</div>
        </div>
        <div style="display: flex; gap: 16px;">
          <div class="legend-item"><span class="legend-dot positive"></span> More in ${data.comparisonLocation.name}</div>
          <div class="legend-item"><span class="legend-dot negative"></span> Less in ${data.comparisonLocation.name}</div>
        </div>
      </div>

      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th style="width: 36px;"></th>
              <th style="min-width: 120px;">Product Name</th>
              <th style="min-width: 70px;">Reference</th>
              <th style="min-width: 80px;">Color</th>
              <th class="warehouse-header" style="min-width: 70px; text-align: center;">${data.warehouseLocation.name}</th>
              <th class="other-header" style="min-width: 70px; text-align: center;">${data.comparisonLocation.name}</th>
              <th style="min-width: 60px; text-align: center;">Diff</th>
              ${sizeHeaders}
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </div>

      <div class="footer">
        <div>
          <strong>Net Difference: <span style="color: ${data.stats.netDifference > 0 ? '#059669' : data.stats.netDifference < 0 ? '#DC2626' : '#64748B'};">${data.stats.netDifference > 0 ? '+' : ''}${data.stats.netDifference}</span></strong>
        </div>
        <div style="text-align: right;">
          This report is confidential and for internal use only.<br>
          Generated by Inventory Management System
        </div>
      </div>

      <script>
        window.onload = () => {
          setTimeout(() => {
            window.print();
            window.onafterprint = () => window.close();
          }, 300);
        };
      </script>
    </body>
    </html>
  `;

  printWindow.document.write(comparisonHtml);
  printWindow.document.close();
}
